import { cn } from "../../lib/utils";
import wordmarkColorSrc from "../../assets/logo/playpro-wordmark.webp";
import wordmarkLightSrc from "../../assets/logo/playpro-wordmark-light.webp";
import markSrc from "../../assets/logo/playpro-mark.webp";

/**
 * Props for `<Logo>` — wordmark/mark/stacked do PlayProSports.
 *
 * Renderiza assets WEBP extraidos diretamente do Key Visual oficial (rebranding 2026).
 * Substitui o SVG tipografico montado a mao por crops 1:1 do KV — tipografia,
 * espacamento e o "0" estilizado em "PR0" sao identicos ao KV aprovado.
 *
 * Caveats:
 * - "wordmark" e "stacked" usam a mesma arte (logo do KV e empilhado: PLAY PR0 em
 *   cima, SPORTS embaixo). Mantemos as duas chaves para nao quebrar consumidores.
 * - "mark" e a glifa compacta "PR0" (somente parte azul + zero estilizado).
 * - "tone=color": logo cheio (PLAY laranja + PR0 azul + SPORTS preto).
 * - "tone=light": versao para fundos escuros (PLAY laranja + PR0 azul claro + SPORTS branco).
 * - "tone=dark": sem asset dedicado no KV; cai no "color" mas aplica filtro CSS pra
 *   converter em monocromatico escuro (mantem contorno legivel sobre fundo claro).
 * - `size` define a altura em pixels; largura segue aspect ratio do asset.
 * - Mobile-first: webp escala nativamente, sem perda perceptivel em 375x667.
 */
export interface LogoProps {
  /** Layout do logo. Default `wordmark`. */
  variant?: "mark" | "wordmark" | "stacked";
  /**
   * Tom de cor:
   * - `color`: paleta brand cheia — default. Uso em fundos claros.
   * - `light`: versao do KV pra fundos escuros (PR0 em azul claro, SPORTS branco).
   * - `dark`: monocromatico escuro via CSS filter — caller controla por classe se quiser.
   */
  tone?: "dark" | "light" | "color";
  /** Altura em pixels. Default 32. */
  size?: number;
  /** Classes extras. */
  className?: string;
}

function resolveSource(
  variant: NonNullable<LogoProps["variant"]>,
  tone: NonNullable<LogoProps["tone"]>,
): string {
  if (variant === "mark") return markSrc;
  if (tone === "light") return wordmarkLightSrc;
  return wordmarkColorSrc;
}

// Aspect ratios reais dos assets gerados a partir do KV (width / height).
// Usados pra reservar largura correta em torno do `size` (height).
const ASPECT_RATIO = {
  mark: 173 / 64, // playpro-mark.webp (~165x56 + padding)
  wordmark: 400 / 92, // playpro-wordmark.webp
  stacked: 400 / 92, // mesma arte do wordmark (KV nao tem layout separado)
} as const;

export function Logo({
  variant = "wordmark",
  tone = "color",
  size = 32,
  className,
}: LogoProps): JSX.Element {
  const src = resolveSource(variant, tone);
  const aspect = ASPECT_RATIO[variant];
  const width = Math.round(size * aspect);

  // Tone "dark" nao tem asset proprio no KV. Aplicamos filter CSS pra escurecer:
  // converte tons claros em escuros mantendo contraste — caller pode override
  // via className se quiser monocromia diferente.
  const darkFilterStyle =
    tone === "dark"
      ? { filter: "brightness(0) saturate(100%)" }
      : undefined;

  return (
    <img
      src={src}
      alt="PlayProSports"
      width={width}
      height={size}
      data-pps-component="logo"
      data-variant={variant}
      data-tone={tone}
      style={{ height: size, width: "auto", ...darkFilterStyle }}
      className={cn("inline-block align-middle select-none", className)}
      draggable={false}
      role="img"
    />
  );
}
